// Library methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import WizardSteps from "../WizardSteps/WizardSteps";
import FormField from "../FormField/FormField";
import ChoiceCard from "../ChoiceCard/ChoiceCard";
import ExamTypePicker from "../ExamTypePicker/ExamTypePicker";
import InfoNote from "../InfoNote/InfoNote";
import ClinicField from "../ClinicField/ClinicField";

// Utils
import {
  EMPTY_MACHINE_FORM_VALUES,
  MachineFormValues,
  MachineTransport,
  buildMachineConfig,
  getMachineTransports,
} from "src/utils/emr/hubConnectionsHelper";
import {
  describeExamTypes,
  defaultExamTypes,
} from "src/utils/emr/examTypeHelper";
import {
  getDeviceDisplayName,
  keepExistingConfig,
} from "src/utils/emr/pacServerHelper";
import { parseAllowedRemoteHosts } from "src/utils/emr/allowedRemoteHostsHelper";

// Hooks
import { useCreateEMRHubConnection } from "src/services/emr/hooks/emr-connections/useCreateEMRHubConnection";
import { useUpdateEMRHubConnection } from "src/services/emr/hooks/emr-connections/useUpdateEMRHubConnection";

// Types
import {
  EMRCatalogDeviceType,
  EMRConnectionType,
  EMRHubType,
} from "src/services/emr/types";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

const STEPS = ["Machine", "Exams", "Details", "Review"];
const OTHER_MANUFACTURER = "Other";
const DIGITS_ONLY = /[^0-9]/g;

type AddMachineModalType = {
  open: boolean;
  clinicId: string | undefined;
  hubId: string | undefined;
  hub?: EMRHubType;
  devices: Array<EMRCatalogDeviceType>;
  listenPort: string;
  sharedFolderPaths: Array<string> | undefined;
  connection?: EMRConnectionType;
  onClose: () => void;
};

export const machineFormValuesFromConnection = (
  connection: EMRConnectionType,
  devices: Array<EMRCatalogDeviceType>,
  listenPort: string
): MachineFormValues => {
  const device = devices.find((item) => item.key === connection.deviceKey);
  const config = connection.config ?? {};

  return {
    name: connection.name,
    manufacturer: device?.manufacturer ?? "",
    deviceKey: device?.key ?? "",
    examTypes: config.examTypes ?? defaultExamTypes(device?.examTypes ?? []),
    transport:
      connection.sourceKind === "SharedFolder"
        ? "SharedFolder"
        : "DicomListener",
    machineAeTitle: config.allowedCallingAeTitles?.[0] ?? "",
    machineIp: (config.allowedRemoteHosts ?? []).join(", "),
    hubAeTitle: config.aeTitle ?? EMPTY_MACHINE_FORM_VALUES.hubAeTitle,
    hubPort: config.port !== undefined ? String(config.port) : listenPort,
    path: config.path ?? "",
  };
};

const hubAddresses = (hub: EMRHubType | undefined) =>
  (hub?.networkInterfaces ?? []).flatMap((network) => network.ipv4 ?? []);

const AddMachineModal = ({
  open,
  clinicId,
  hubId,
  hub,
  devices,
  listenPort,
  sharedFolderPaths,
  connection,
  onClose,
}: AddMachineModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const isEdit = Boolean(connection);

  const initialValues = useCallback(
    (): MachineFormValues =>
      connection
        ? machineFormValuesFromConnection(connection, devices, listenPort)
        : { ...EMPTY_MACHINE_FORM_VALUES, hubPort: listenPort },
    [connection, devices, listenPort]
  );

  const [step, setStep] = useState(0);
  const [values, setValues] = useState<MachineFormValues>(initialValues);
  const targetClinicId = connection?.clinicId ?? clinicId;
  const [copied, setCopied] = useState(false);

  const { mutate: create, isPending: isCreating } = useCreateEMRHubConnection(
    targetClinicId,
    hubId
  );
  const { mutate: update, isPending: isUpdating } =
    useUpdateEMRHubConnection(targetClinicId);
  const isPending = isCreating || isUpdating;

  const machines = useMemo(
    () => devices.filter((device) => device.kind === "Device"),
    [devices]
  );

  const manufacturerOptions = useMemo(
    () =>
      Array.from(new Set(machines.map((device) => device.manufacturer)))
        .sort((left, right) =>
          left === OTHER_MANUFACTURER
            ? 1
            : right === OTHER_MANUFACTURER
              ? -1
              : left.localeCompare(right)
        )
        .map((manufacturer) => ({
          displayName:
            manufacturer === OTHER_MANUFACTURER
              ? t("Other / Not listed")
              : manufacturer,
          value: manufacturer,
        })),
    [machines, t]
  );

  const modelOptions = useMemo(
    () =>
      machines
        .filter((device) => device.manufacturer === values.manufacturer)
        .sort((left, right) => left.model.localeCompare(right.model))
        .map((device) => ({ displayName: device.model, value: device.key })),
    [machines, values.manufacturer]
  );

  const device = machines.find((item) => item.key === values.deviceKey);
  const transports = getMachineTransports(device);
  const deviceName = getDeviceDisplayName(device);
  const configJson = useMemo(() => buildMachineConfig(values), [values]);
  const remoteHosts = parseAllowedRemoteHosts(values.machineIp);
  const isDuplicatePath =
    values.transport === "SharedFolder" &&
    values.path.trim() !== connection?.config?.path &&
    Boolean(sharedFolderPaths?.includes(values.path.trim()));
  const hubIps = hubAddresses(hub);

  const updateValues = useCallback(
    (changes: Partial<MachineFormValues>) =>
      setValues((previous) => ({ ...previous, ...changes })),
    []
  );

  const selectDevice = (deviceKey: string) => {
    const selected = machines.find((item) => item.key === deviceKey);
    const available = getMachineTransports(selected);

    updateValues({
      deviceKey,
      examTypes: defaultExamTypes(selected?.examTypes ?? []),
      transport: available.includes("DicomListener")
        ? "DicomListener"
        : "SharedFolder",
      name:
        !values.name || values.name === device?.model
          ? selected && selected.manufacturer !== OTHER_MANUFACTURER
            ? selected.model
            : ""
          : values.name,
    });
  };

  const resetForm = useCallback(() => {
    setStep(0);
    setCopied(false);
    setValues(initialValues());
  }, [initialValues]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const isStepValid = [
    Boolean(device) && values.name.trim().length > 0,
    values.transport === "SharedFolder" || values.examTypes.length > 0,
    configJson !== null && !isDuplicatePath,
    configJson !== null,
  ][step];

  const handleCreate = () => {
    if (!targetClinicId || !device || configJson === null) return;

    const payload = {
      clinicId: targetClinicId,
      name: values.name.trim(),
      sourceKind: values.transport,
      direction: "Inbound" as const,
      enabled: connection?.enabled ?? true,
      deviceKey: device.key,
    };

    if (connection) {
      update(
        {
          ...payload,
          connectionId: connection.connectionId,
          configJson:
            connection.sourceKind === values.transport
              ? keepExistingConfig(connection.config, configJson)
              : configJson,
        },
        { onSuccess: handleClose }
      );
      return;
    }

    create({ ...payload, configJson }, { onSuccess: handleClose });
  };

  const destinationLines: Array<[string, string]> = [
    [
      t("Host"),
      hubIps.length > 0 ? hubIps.join(", ") : t("The hub PC's IP address"),
    ],
    [t("Port"), values.hubPort],
    [t("Called AE title"), values.hubAeTitle],
    [t("Calling AE title"), values.machineAeTitle],
  ];

  const copyForTechnician = () => {
    const text = destinationLines
      .map(([label, value]) => `${label}: ${value}`)
      .join("\n");
    navigator.clipboard
      ?.writeText(text)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  const transportLabel = (transport: MachineTransport) =>
    transport === "DicomListener"
      ? t("It sends them to the hub")
      : t("It saves files to a folder");

  return (
    <DefaultModal
      isOpen={open}
      onClose={handleClose}
      title={isEdit ? t("Edit machine") : t("Add a machine")}
      maxWidth="sm"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
            width: "100%",
          }}
        >
          <Button
            sx={ModalCustomCancel}
            onClick={step === 0 ? handleClose : () => setStep(step - 1)}
          >
            {step === 0 ? t("Cancel") : t("Back")}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              sx={ModalCustomConfirm}
              disabled={!isStepValid}
              onClick={() => setStep(step + 1)}
            >
              {t("Next")}
            </Button>
          ) : (
            <Button
              sx={ModalCustomConfirm}
              disabled={!isStepValid || isPending}
              onClick={handleCreate}
            >
              {isEdit ? t("Save") : t("Create connection")}
            </Button>
          )}
        </Grid>
      }
    >
      <WizardSteps steps={STEPS.map((item) => t(item))} current={step} />
      <Box display="flex" flexDirection="column" gap="16px" p="16px">
        {step === 0 && (
          <>
            {!isEdit && (
              <InfoNote>
                {t(
                  "A machine that only sends to the PAC server needs nothing here: its exams come in through the worklist's query."
                )}
              </InfoNote>
            )}
            <Box display="flex" gap="8px">
              <FormField
                label={t("Manufacturer")}
                labelId="add-machine-manufacturer-label"
                required
              >
                <SelectDropdown
                  id="add-machine-manufacturer-dropdown"
                  labelId="add-machine-manufacturer-label"
                  dropdownOptions={manufacturerOptions}
                  value={
                    manufacturerOptions.find(
                      (option) => option.value === values.manufacturer
                    )?.displayName ?? ""
                  }
                  handleChange={(event) => {
                    const manufacturer = manufacturerOptions.find(
                      (option) => option.displayName === event.target.value
                    )?.value;
                    if (!manufacturer) return;

                    const onlyModel = machines.filter(
                      (item) => item.manufacturer === manufacturer
                    );
                    updateValues({ manufacturer, deviceKey: "", name: "" });
                    if (onlyModel.length === 1) {
                      setValues((previous) => ({
                        ...previous,
                        deviceKey: onlyModel[0].key,
                        examTypes: defaultExamTypes(onlyModel[0].examTypes),
                        transport: getMachineTransports(onlyModel[0]).includes(
                          "DicomListener"
                        )
                          ? "DicomListener"
                          : "SharedFolder",
                        name: "",
                      }));
                    }
                  }}
                />
              </FormField>
              <FormField
                label={t("Model")}
                labelId="add-machine-model-label"
                required
              >
                <SelectDropdown
                  id="add-machine-model-dropdown"
                  labelId="add-machine-model-label"
                  disabled={!values.manufacturer}
                  dropdownOptions={modelOptions}
                  value={device?.model ?? ""}
                  handleChange={(event) => {
                    const deviceKey = modelOptions.find(
                      (option) => option.displayName === event.target.value
                    )?.value;
                    if (deviceKey) selectDevice(deviceKey);
                  }}
                />
              </FormField>
            </Box>

            {device && (
              <Box
                data-testid="add-machine-device-summary"
                display="flex"
                flexDirection="column"
                gap="6px"
              >
                <Typography fontSize="13px" fontWeight={800}>
                  {t("{{device}} makes {{examTypes}}", {
                    device: deviceName,
                    examTypes: describeExamTypes(device.examTypes, t),
                  })}
                </Typography>
                <Typography fontSize="12px" color={mutedText}>
                  {[
                    device.dicomSupport === "Yes" && t("Can send over DICOM"),
                    device.dicomSupport === "Maybe" &&
                      t("DICOM needs the vendor's software or licence"),
                    device.transports.includes("SharedFolder") &&
                      t("Can export files to a folder"),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </Box>
            )}

            <FormField
              label={t("Name")}
              htmlFor="add-machine-name-input"
              required
            >
              <TextInputField
                id="add-machine-name-input"
                placeholder={t("e.g. Cirrus, room 2")}
                value={values.name}
                maxLength={100}
                onChange={(event) => updateValues({ name: event.target.value })}
              />
            </FormField>
            <ClinicField
              idPrefix="add-machine"
              hub={isEdit ? undefined : hub}
              clinicId={targetClinicId}
            />
            <Typography fontSize="12px" color={mutedText}>
              {t(
                "Only machines we support are listed, and the next step offers only what this one can do."
              )}
            </Typography>
          </>
        )}

        {step === 1 && device && (
          <>
            {values.transport === "DicomListener" && (
              <Box display="flex" flexDirection="column" gap="8px">
                <Typography fontSize="15px" fontWeight={800}>
                  {t("Which exams should it bring in?")}
                </Typography>
                <Typography fontSize="12px" color={mutedText}>
                  {t(
                    "Only what this model makes is listed. The hub turns anything else away."
                  )}
                </Typography>
                <ExamTypePicker
                  options={device.examTypes}
                  value={values.examTypes}
                  onChange={(examTypes) => updateValues({ examTypes })}
                />
              </Box>
            )}
            <Box display="flex" flexDirection="column" gap="8px">
              <Typography fontSize="15px" fontWeight={800}>
                {t("How does the machine send them?")}
              </Typography>
              {transports.map((transport) => (
                <ChoiceCard
                  key={transport}
                  name="add-machine-transport"
                  value={transport}
                  checked={values.transport === transport}
                  title={transportLabel(transport)}
                  badge={
                    transport === "DicomListener" &&
                    device.dicomSupport === "Yes"
                      ? t("Recommended")
                      : undefined
                  }
                  description={
                    transport === "DicomListener"
                      ? t(
                          "DICOM. You point the machine at the hub; the next step shows the values to enter."
                        )
                      : t(
                          "PDF or DICOM export to a folder the hub watches. The exam type is read from each file."
                        )
                  }
                  onSelect={() => updateValues({ transport })}
                />
              ))}
              {device.dicomSupport === "Maybe" &&
                transports.includes("DicomListener") && (
                  <InfoNote testId="add-machine-dicom-maybe" tone="warning">
                    {t("DICOM needs the vendor's software or licence")}
                  </InfoNote>
                )}
            </Box>
          </>
        )}

        {step === 2 && values.transport === "DicomListener" && (
          <>
            <Typography fontSize="15px" fontWeight={800}>
              {t("Who may send")}
            </Typography>
            <Box display="flex" gap="8px">
              <FormField
                label={t("Machine AE title")}
                htmlFor="add-machine-ae-title-input"
                required
              >
                <TextInputField
                  id="add-machine-ae-title-input"
                  placeholder={t("e.g. CIRRUS01")}
                  value={values.machineAeTitle}
                  maxLength={16}
                  onChange={(event) =>
                    updateValues({ machineAeTitle: event.target.value })
                  }
                />
              </FormField>
              <FormField
                label={t("Machine IP address")}
                htmlFor="add-machine-ip-input"
              >
                <TextInputField
                  id="add-machine-ip-input"
                  placeholder={t("Optional, e.g. 10.0.0.25")}
                  value={values.machineIp}
                  maxLength={64}
                  error={remoteHosts.invalid.length > 0}
                  helperText={
                    remoteHosts.invalid.length > 0
                      ? t("Not a valid IP address or CIDR range.")
                      : undefined
                  }
                  onChange={(event) =>
                    updateValues({ machineIp: event.target.value })
                  }
                />
              </FormField>
            </Box>
            <Typography fontSize="12px" color={mutedText}>
              {t(
                "The name the machine uses for itself on the network. Anything calling with a different name is turned away."
              )}
            </Typography>
            <Typography fontSize="15px" fontWeight={800}>
              {t("The hub listens as")}
            </Typography>
            <Box display="flex" gap="8px">
              <FormField
                label={t("Hub AE title")}
                htmlFor="add-machine-hub-ae-title-input"
                required
              >
                <TextInputField
                  id="add-machine-hub-ae-title-input"
                  placeholder="EMRHUB"
                  value={values.hubAeTitle}
                  maxLength={16}
                  onChange={(event) =>
                    updateValues({ hubAeTitle: event.target.value })
                  }
                />
              </FormField>
              <FormField
                label={t("Hub port")}
                htmlFor="add-machine-hub-port-input"
                required
              >
                <TextInputField
                  id="add-machine-hub-port-input"
                  placeholder="104"
                  value={values.hubPort}
                  maxLength={5}
                  onChange={(event) =>
                    updateValues({
                      hubPort: event.target.value.replace(DIGITS_ONLY, ""),
                    })
                  }
                />
              </FormField>
            </Box>
            <Box
              data-testid="add-machine-destination"
              sx={{
                border: "1px dashed #A9ACBC",
                borderRadius: "8px",
                padding: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Typography fontSize="13px" fontWeight={800}>
                {t("Enter this on the machine as its DICOM destination")}
              </Typography>
              <Box display="flex" gap="24px" flexWrap="wrap">
                {destinationLines.map(([label, value]) => (
                  <Box key={label} display="flex" flexDirection="column">
                    <Typography fontSize="11px" color={mutedText}>
                      {label}
                    </Typography>
                    <Typography
                      fontSize="14px"
                      fontWeight={700}
                      fontFamily="Consolas, 'Courier New', monospace"
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box display="flex" gap="12px" alignItems="center">
                <Button
                  data-testid="add-machine-copy-for-technician"
                  sx={ModalCustomCancel}
                  onClick={copyForTechnician}
                >
                  {t("Copy for the technician")}
                </Button>
                {copied && (
                  <Typography fontSize="12px" color={mutedText} role="status">
                    {t("Copied")}
                  </Typography>
                )}
              </Box>
              {hubIps.length > 1 && (
                <Typography fontSize="12px" color={mutedText}>
                  {t(
                    "The hub PC has more than one address. Use the one on the machines' network."
                  )}
                </Typography>
              )}
            </Box>
          </>
        )}

        {step === 2 && values.transport === "SharedFolder" && (
          <FormField
            label={t("Folder")}
            htmlFor="add-machine-path-input"
            required
          >
            <TextInputField
              id="add-machine-path-input"
              placeholder="\\server\share\exports"
              value={values.path}
              maxLength={260}
              error={isDuplicatePath}
              helperText={
                isDuplicatePath
                  ? t("An EMR connection already exists for this path.")
                  : t(
                      "A local folder on the hub PC or a network share. The hub reads it with its own Windows account."
                    )
              }
              onChange={(event) => updateValues({ path: event.target.value })}
            />
          </FormField>
        )}

        {step === 3 && device && (
          <Box
            data-testid="add-machine-review"
            sx={{
              padding: "12px",
              borderRadius: "8px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#2A2C3F" : "rgba(0,0,0,0.05)",
            }}
          >
            <Typography fontSize="14px" lineHeight={1.55}>
              {values.transport === "DicomListener"
                ? t(
                    "{{name}} sends {{examTypes}} exams from a {{device}} straight to the hub over DICOM, as {{aeTitle}} : {{port}}, accepting only {{callingAeTitle}}.",
                    {
                      name: values.name.trim(),
                      examTypes: describeExamTypes(values.examTypes, t),
                      device: deviceName,
                      aeTitle: values.hubAeTitle,
                      port: values.hubPort,
                      callingAeTitle: values.machineAeTitle,
                    }
                  )
                : t(
                    "{{name}}: exams from a {{device}} are picked up from {{path}}.",
                    {
                      name: values.name.trim(),
                      device: deviceName,
                      path: values.path.trim(),
                    }
                  )}
            </Typography>
          </Box>
        )}
      </Box>
    </DefaultModal>
  );
};

export default AddMachineModal;
